import React, { useEffect, useState } from 'react'
import Switch from 'react-switch'
import {
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Row,
  Alert,
} from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  bottledContainerSizeOptions,
  concentrateContainerSizeOptions,
  flavorOptions,
  juiceTypeOptions,
} from './constants'
import Select from 'react-select'
import PropTypes from 'prop-types'
import { isNotAlpha } from 'context/Utilities'

const JuicePackageReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    fkCategory: item.fkCategory,
    fkSubCategory: item.fkSubCategory,
    JuiceFlavor: item.JuiceFlavor,
    ContainerType: item.ContainerType,
    VitaminCCertification: item.VitaminCCertification,
    OneHundredPercentJuiceCertification:
      item.OneHundredPercentJuiceCertification,
    FrontLabelCertification: item.FrontLabelCertification,
    JuiceType: item.JuiceType,
    BlendFlavor: item.BlendFlavor,
  })

  const methods = useFormContext()
  const {
    setSelectReviewer,
    setSwitchReviewer,
    onChangeReviewer,
    asterisk,
  } = useFormUtils()

  const optionsReset = () => {
    methods.reset(
      {
        ContainerType: '',
      },
      {
        errors: false, // errors will not be reset
        dirtyFields: false, // dirtyFields will not be reset
        dirty: false, // dirty will not be reset
        isSubmitted: false,
        touched: false,
        isValid: false,
        submitCount: false,
      }
    )
  }

  useEffect(() => {
    if (item !== null) {
      const {
        JuiceFlavor,
        ContainerType,
        VitaminCCertification,
        OneHundredPercentJuiceCertification,
        FrontLabelCertification,
        JuiceType,
        BlendFlavor,
      } = item
      setPackaging({
        JuiceFlavor,
        ContainerType,
        VitaminCCertification,
        OneHundredPercentJuiceCertification,
        FrontLabelCertification,
        JuiceType,
        BlendFlavor,
      })
    }
    // eslint-disable-next-line
  }, [item])

  return (
    <>
      <Row>
        <Col xs={12} md={6} lg={6}>
          <p>
            <strong>Juice Type {asterisk}</strong>
          </p>
          <ButtonGroup>
            <Button
              outline
              color="success"
              className="btn-round"
              onClick={() => {
                optionsReset()
                item.JuiceType = juiceTypeOptions[0]
                item.ContainerType = bottledContainerSizeOptions[1].value
              }}
              active={item.JuiceType === 'Bottled' && !isDisabled}
              disabled={isDisabled}
              size="sm"
            >
              {juiceTypeOptions[0]}
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button
              outline
              color="success"
              className="btn-round"
              onClick={() => {
                optionsReset()
                item.JuiceType = juiceTypeOptions[1]
                item.ContainerType = ''
              }}
              active={item.JuiceType === 'Concentrate' && !isDisabled}
              disabled={isDisabled}
              style={{
                color: 'black !important',
              }}
              size="sm"
            >
              {juiceTypeOptions[1]}
            </Button>
          </ButtonGroup>
          <br />
          <p>
            <strong>Juice Flavor {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            defaultValue={{
              label: packaging.JuiceFlavor,
              value: packaging.JuiceFlavor,
            }}
            options={flavorOptions}
            name="JuiceFlavor"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Juice Flavor'}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              setPackaging({ ...packaging, JuiceFlavor: selected.value })
              return selected
            }}
            isDisabled={isDisabled}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="JuiceFlavor"
              message={'⚠ This selection is required.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
          <br />
          {packaging.JuiceFlavor === 'Blend' && (
            <FormGroup>
              <p>
                <strong>Blend Flavor {asterisk}</strong>
              </p>
              <Controller
                as={Input}
                name="BlendFlavor"
                placeholder={'Blend Flavor'}
                defaultValue={packaging.BlendFlavor}
                control={methods.control}
                rules={{
                  required: {
                    value: true,
                    message: '⚠ Blend flavor is required.',
                  },
                }}
                onKeyDown={e => {
                  if (isNotAlpha(e.key) && e.key !== ' ') {
                    e.preventDefault()
                  }
                }}
                onChange={([event]) => {
                  onChangeReviewer(item, event)
                  setPackaging({
                    ...packaging,
                    BlendFlavor: event.target.value,
                  })
                  return event
                }}
                disabled={isDisabled}
              />
              <p className={'text-danger'}>
                <ErrorMessage
                  errors={methods.errors}
                  name="BlendFlavor"
                  as={<Alert color={'danger'} className={'mt-3'} />}
                />
              </p>
            </FormGroup>
          )}
          <br />
          <FormGroup>
            <p>
              <strong>Size {asterisk}</strong>
            </p>
            {item.JuiceType === 'Concentrate' && (
              <Controller
                as={Select}
                className="react-select primary"
                classNamePrefix="react-select"
                options={concentrateContainerSizeOptions}
                defaultValue={{
                  label: packaging.ContainerType,
                  value: packaging.ContainerType,
                }}
                name="ContainerType"
                control={methods.control}
                rules={{ required: true }}
                placeholder={'Select Size'}
                onChange={([selected, value]) => {
                  setSelectReviewer(item, selected, value)
                  return selected
                }}
                isDisabled={isDisabled}
              />
            )}
            {item.JuiceType === 'Bottled' && (
              <Input
                defaultValue={bottledContainerSizeOptions[1].value}
                disabled
              />
            )}

            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="ContainerType"
                message={'⚠ This selection is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        </Col>
        <Col xs={12} md={6} lg={6}>
          {/*           <p>
            <strong>80% (or more) Vitamin C {asterisk}</strong>
          </p>
          <Controller
            as={Switch}
            name="VitaminCCertification"
            id="VitaminCCertification"
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.VitaminCCertification}
            rules={{
              validate: VitaminCCertification => VitaminCCertification === true,
            }}
            disabled={isDisabled}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="VitaminCCertification"
              message={'⚠ Product must be 80% (or more) Vitamin C.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
          <hr />
          <p>
            <strong>Is 100% Juice {asterisk}</strong>
          </p>
          <Controller
            as={Switch}
            name="OneHundredPercentJuiceCertification"
            id="OneHundredPercentJuiceCertification"
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.OneHundredPercentJuiceCertification}
            rules={{
              validate: OneHundredPercentJuiceCertification =>
                OneHundredPercentJuiceCertification === true,
            }}
            disabled={isDisabled}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="OneHundredPercentJuiceCertification"
              message={'⚠ Product must be 100% juice (unsweetened).'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p> 
          <hr /> */}
          <p>
            <strong>
              Front label includes statements indicating the product contains
              80% (or more) Vitamin C and is 100% Juice. Orange juice and white
              grapefruit juice are not required to have Vitamin C statement on
              the front label; however, the submitter must check this box for
              submission purposes. {asterisk}
            </strong>
          </p>
          <Controller
            as={Switch}
            name="FrontLabelCertification"
            id="FrontLabelCertification"
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              return checked
            }}
            control={methods.control}
            defaultValue={packaging.FrontLabelCertification}
            rules={{
              validate: FrontLabelCertification =>
                FrontLabelCertification === true,
            }}
            disabled={isDisabled}
          />
          <br />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="FrontLabelCertification"
              message={'⚠ Product must meet labeling requirements.'}
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </Col>
      </Row>
    </>
  )
}

JuicePackageReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default JuicePackageReview
